// Other Imports Removed for Brevity

import { IconCaretDown, IconCheck } from '@tabler/icons-react'
import { forwardRef, useMemo, useState } from 'react'
import { cn } from '../utils/cn'
import { Button, ButtonProps } from './Button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from './Command'
import { Popover, PopoverTrigger, PopoverContent } from './Popover'
import { ScrollArea } from './ScrollArea'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './Tooltip'

export interface ComboboxOption {
  value: string
  label: React.ReactNode | string
  wildcardHidden?: boolean
  description?: string
}

type ComboboxPropsSingle = {
  withTooltipValue?: boolean
  readonly?: boolean
  options: ComboboxOption[]
  emptyText?: string
  clearable?: boolean
  selectPlaceholder?: string
  searchPlaceholder?: string
  multiple?: false
  value?: string
  onValueChange?: (value: string) => void
  classNames?: {
    button?: string
    floatContainer?: string
  }
  properties?: {
    button?: ButtonProps
  }
}

type ComboboxPropsMultiple = {
  withTooltipValue?: boolean
  readonly?: boolean
  withSelectAll?: boolean
  options: ComboboxOption[]
  emptyText?: string
  clearable?: boolean
  selectPlaceholder?: string
  searchPlaceholder?: string
  multiple: true
  value?: string[]
  onValueChange?: (value: string[]) => void
  classNames?: {
    button?: string
    container?: string
    floatContainer?: string
  }
  properties?: {
    button?: ButtonProps
  }
}

const normalizeString = (value: string = '') =>
  value
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

export type ComboboxProps = ComboboxPropsSingle | ComboboxPropsMultiple

export const handleSingleSelect = (
  props: ComboboxPropsSingle,
  option: ComboboxOption,
) => {
  if (props.clearable) {
    props.onValueChange?.(option.value === props.value ? '' : option.value)
  } else {
    props.onValueChange?.(option.value)
  }
}

export const handleMultipleSelect = (
  props: ComboboxPropsMultiple,
  option: ComboboxOption,
) => {
  if (props.value?.includes(option.value)) {
    if (!props.clearable && props.value.length === 1) return false
    props.onValueChange?.(props.value.filter((value) => value !== option.value))
  } else {
    props.onValueChange?.([...(props.value ?? []), option.value])
  }
}

const isOptionSelected = (
  props: Pick<ComboboxProps, 'value' | 'multiple'>,
  option: ComboboxOption,
) => {
  if (props.multiple) return props.value?.includes(option.value)

  return props.value === option.value
}

export const ComboboxSelect = forwardRef(
  (
    {
      properties,
      classNames,
      readonly,
      withTooltipValue = false,
      options: unsortedOptions,
      ...props
    }: ComboboxProps,
    ref: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const [open, setOpen] = useState(false)

    const options = useMemo(
      () =>
        unsortedOptions.sort((a, b) => {
          if (isOptionSelected(props, a) && !isOptionSelected(props, b))
            return -1

          if (!isOptionSelected(props, a) && isOptionSelected(props, b))
            return 1

          return 0
        }),
      [unsortedOptions, JSON.stringify(props.value || {})],
    )

    return (
      <Popover
        open={open}
        onOpenChange={(open) => {
          if (readonly) return

          setOpen(open)
        }}
      >
        <PopoverTrigger asChild>
          <Button
            role='combobox'
            variant='outline'
            aria-expanded={open}
            className={cn(
              'w-full justify-between hover:bg-secondary/20 active:scale-100',
              classNames?.button,
            )}
            {...properties?.button}
          >
            <span className='line-clamp-1 text-left font-normal'>
              {props.multiple && props.value && props.value.length === 1 && (
                <span role='combobox-value' className='mr-2'>
                  {props.value
                    .map(
                      (value) =>
                        options.find((opt) => opt.value === value)?.label ||
                        'Opción no encontrada',
                    )
                    .join(', ')}
                </span>
              )}

              {props.multiple && props.value && props.value.length > 1 && (
                <>
                  {withTooltipValue ? (
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger>
                          <span role='combobox-value' className='mr-2'>
                            {props.value.length} opciones seleccionadas
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <ul className='flex flex-col space-y-1'>
                            {props.value.map((value, index) => {
                              const option = options.find(
                                (opt) => opt.value === value,
                              )!

                              return (
                                <li className='text-xs font-normal' key={index}>
                                  {option.label}
                                </li>
                              )
                            })}
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <span role='combobox-value' className='mr-2'>
                      {props.value.length} opciones seleccionadas
                    </span>
                  )}
                </>
              )}

              {!props.multiple &&
                props.value &&
                props.value !== '' &&
                options.find((option) => option.value === props.value)?.label}

              {(!props.value || props.value.length === 0) &&
                (props.selectPlaceholder ?? 'Select an option')}
            </span>
            <IconCaretDown
              className={cn(
                'ml-2 h-4 w-4 shrink-0 rotate-0 opacity-50 transition-transform',
                open && 'rotate-180',
              )}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align='start'
          className={cn('p-0', classNames?.floatContainer)}
        >
          <Command
            filter={(value, search) => {
              const label = options
                .find((opt) => opt.value === value)
                ?.label?.toString()

              if (normalizeString(label).includes(normalizeString(search)))
                return 1

              return 0
            }}
          >
            <CommandInput
              ref={ref}
              placeholder={props.searchPlaceholder ?? 'Busca una opción'}
            />
            <CommandEmpty>
              {props.emptyText ?? 'No se encontraron resultados'}
            </CommandEmpty>
            <CommandGroup>
              <ScrollArea orientation='vertical'>
                <div className={cn('max-h-60')}>
                  {props.multiple && props.withSelectAll && (
                    <CommandItem
                      value='_all'
                      onSelect={() => {
                        const selectedAll =
                          props.value?.length === options.length

                        if (props.multiple && props.onValueChange) {
                          if (selectedAll) {
                            props.onValueChange([])
                          }

                          if (!selectedAll) {
                            props.onValueChange(
                              options
                                .filter((opt) => !opt.wildcardHidden)
                                .map((opt) => opt.value),
                            )
                          }
                        }
                      }}
                    >
                      <IconCheck
                        className={cn('mr-2 h-4 w-4 opacity-0 flex-shrink-0', {
                          'opacity-100': props.value?.length === options.length,
                        })}
                      />
                      Seleccionar todos
                    </CommandItem>
                  )}

                  {options.map((option) => (
                    <CommandItem
                      value={option.value.toLowerCase().trim()}
                      onSelect={(selectedValue) => {
                        const option = options.find(
                          (option) =>
                            option.value.toLowerCase().trim() === selectedValue,
                        )

                        if (!option) return null

                        if (props.multiple) {
                          handleMultipleSelect(
                            {
                              ...props,
                              options,
                            },
                            option,
                          )
                        } else {
                          handleSingleSelect(
                            {
                              ...props,
                              options,
                            },
                            option,
                          )

                          setOpen(false)
                        }
                      }}
                    >
                      <IconCheck
                        className={cn(
                          'mr-2 h-4 w-4 opacity-0 flex-shrink-0',
                          !props.multiple &&
                            props.value === option.value &&
                            'opacity-100',
                          props.multiple &&
                            props.value?.includes(option.value) &&
                            'opacity-100',
                        )}
                      />
                      <div className='flex flex-col space-y-1'>
                        <span>{option.label}</span>
                        {option.description && (
                          <span className='text-xs text-gray-500'>
                            {option.description}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  ))}
                </div>
              </ScrollArea>
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
)
